import { AppState, AppStateStatus } from 'react-native';
import { EventChannel, eventChannel, Task } from 'redux-saga';
import {
	call,
	cancel,
	delay,
	fork,
	put,
	race,
	select,
	spawn,
	take,
	takeEvery,
	takeLatest,
	takeLeading,
} from 'redux-saga/effects';
import type { Socket } from 'socket.io-client';

import {
	cancelDuelReward,
	claimDuelReward,
	DuelHistoryResponse,
	DuelHubResponse,
	DuelPlayerRewardDto,
	DuelPlayerSeasonDto,
	DuelResultResponse,
	DuelRewardTransitionDto,
	DuelSeasonLeaderboardResponse,
	DuelSeasonProgressResponse,
	DuelSnapshotDto,
	getDuelHistory,
	getDuelHub,
	getDuelResult,
	getDuelRewards,
	getDuelSeason,
	getDuelSeasonRanking,
	getDuelSeasons,
	requestDuelReward,
} from '../../services/api';
import { closeDuelsSocket, openDuelsSocket } from '../../services/duelsSocket';
import { flattenApiMessage } from '../../utils/helpers/apiErrorMessage';
import { getUserFacingHttpErrorKey } from '../../utils/helpers/httpErrorKey';
import { loginSuccess, logout } from '../auth/authActions';
import { selectIsLogged } from '../auth/authReducer';
import {
	duelResultBlur,
	duelResultFailure,
	duelResultFocus,
	duelResultRetry,
	duelResultSuccess,
	duelRewardCancelSubmit,
	duelRewardClaimFailure,
	duelRewardClaimSubmit,
	duelRewardRequestSubmit,
	duelRewardTransitionFailure,
	duelRewardTransitionSuccess,
	duelsConnectionChanged,
	duelsCurrentCleared,
	duelsCurrentReady,
	duelsCurrentRefresh,
	duelsHistoryFailure,
	duelsHistoryLoadMore,
	duelsHistoryRequest,
	duelsHistorySuccess,
	duelsHubFailure,
	duelsHubRequest,
	duelsHubSuccess,
	duelsJoinFailure,
	duelsJoinRequest,
	duelsJoinSuccess,
	duelsPlayStop,
	duelsQuitFailure,
	duelsQuitRequest,
	duelsQuitSettled,
	duelsRankingFailure,
	duelsRankingRequest,
	duelsRankingSuccess,
	duelsReconnectRequest,
	duelsRewardsFailure,
	duelsRewardsRequest,
	duelsRewardsSuccess,
	duelsScreenMounted,
	duelsScreenUnmounted,
	duelsSeasonFailure,
	duelsSeasonRequest,
	duelsSeasonsSuccess,
	duelsSeasonSuccess,
	duelsSnapshotReceived,
	duelsSocketError,
} from './duelsActions';
import {
	DuelHistoryFilter,
	DuelRankingScope,
	DuelsState,
	selectDuelResult,
	selectDuelRewardOverlays,
	selectDuelRewards,
	selectDuelsConnection,
	selectDuelsHistory,
	selectDuelsHub,
	selectDuelSnapshot,
} from './duelsReducer';

const GAME_TYPE = 'svara';
const JOIN_TIMEOUT_MS = 15000;
const QUIT_TIMEOUT_MS = 15000;
const HISTORY_PAGE_SIZE = 20;
const RESULT_RETRY_DELAYS_MS = [1000, 2000, 4000, 8000, 16000, 30000];
const RESULT_MAX_LAGGING_ATTEMPTS = RESULT_RETRY_DELAYS_MS.length + 10;
const STALE_CURRENT_MS = 30000;

const LOAD_ERROR_KEY = 'T_SITE_DUELS_ERROR_LOAD';
const JOIN_ERROR_KEY = 'T_SITE_DUELS_ERROR_JOIN_FAILED';
const JOIN_TIMEOUT_KEY = 'T_SITE_DUELS_ERROR_JOIN_TIMEOUT';
const UNAVAILABLE_KEY = 'T_SITE_DUELS_ERROR_UNAVAILABLE';
const QUIT_ERROR_KEY = 'T_SITE_DUELS_HUD_LEAVE_FAILED';
const RESULT_NOT_FOUND_KEY = 'T_SITE_DUELS_ERROR_RESULT_NOT_FOUND';
const REWARD_ERROR_KEY = 'T_SITE_DUELS_REWARD_ERROR';
const REWARD_REFRESH_DELAYS_MS = [1000, 2000, 4000];

const REWARD_CODE_KEYS: Record<string, string> = {
	duel_reward_window_closed: 'T_SITE_DUELS_REWARD_ERROR_WINDOW_CLOSED',
	duel_reward_request_limit: 'T_SITE_DUELS_REWARD_ERROR_LIMIT',
	duel_reward_not_requestable: 'T_SITE_DUELS_REWARD_ERROR_NOT_REQUESTABLE',
	duel_reward_details_incomplete: 'T_SITE_DUELS_REWARD_ERROR_DETAILS',
	duel_reward_busy: 'T_SITE_DUELS_REWARD_ERROR_BUSY',
	duel_reward_not_found: 'T_SITE_DUELS_REWARD_ERROR_NOT_FOUND',
	duel_reward_not_claimable: 'T_SITE_DUELS_REWARD_ERROR_NOT_CLAIMABLE',
	duel_reward_unavailable: 'T_SITE_DUELS_REWARD_ERROR',
};

const REWARD_CODE_PREFIX_KEYS: [string, string][] = [
	['duel_reward_ineligible:', 'T_SITE_DUELS_REWARD_ERROR_INELIGIBLE'],
	['duel_reward_invalid_status:', 'T_SITE_DUELS_REWARD_ERROR_STATUS_CHANGED'],
];

export const rewardErrorKey = (error: any): string => {
	const raw = error?.response?.data?.message;
	if (Array.isArray(raw)) return 'T_SITE_DUELS_REWARD_ERROR_DETAILS';

	const code = flattenApiMessage(raw);
	if (REWARD_CODE_KEYS[code]) return REWARD_CODE_KEYS[code];
	const prefixed = REWARD_CODE_PREFIX_KEYS.find(([prefix]) => code.startsWith(prefix));
	if (prefixed) return prefixed[1];

	return httpErrorKey(error, REWARD_ERROR_KEY);
};

const JOIN_CODE_KEYS: Record<string, string> = {
	duel_join_refused: 'T_SITE_DUELS_ERROR_JOIN_REFUSED',
	duel_matchmaking_unavailable: 'T_SITE_DUELS_ERROR_MATCHMAKING_UNAVAILABLE',
	duel_matchmaking_busy: 'T_SITE_DUELS_ERROR_MATCHMAKING_BUSY',
	duel_type_not_available: 'T_SITE_DUELS_ERROR_TYPE_NOT_AVAILABLE',
	duel_game_type_not_supported: 'T_SITE_DUELS_ERROR_TYPE_NOT_AVAILABLE',
	duel_join_failed: JOIN_ERROR_KEY,
};

type SocketEvent =
	| { type: 'connect' }
	| { type: 'disconnect'; reason: string }
	| { type: 'connectError' }
	| { type: 'updated'; snapshot: DuelSnapshotDto }
	| { type: 'joined'; snapshot: DuelSnapshotDto }
	| { type: 'error'; message: string; code?: string }
	| { type: 'ready' }
	| { type: 'foreground' };

let activeSocket: Socket | null = null;

const httpErrorKey = (error: any, fallbackKey: string): string => {
	const resolved = getUserFacingHttpErrorKey(error, {
		serverUnavailableKey: fallbackKey,
		authKey: fallbackKey,
		networkKey: fallbackKey,
		clientErrorKey: fallbackKey,
		fallbackKey,
	});
	return resolved.startsWith('T_') ? resolved : fallbackKey;
};

const isSnapshot = (value: unknown): value is DuelSnapshotDto =>
	!!value && typeof value === 'object' && typeof (value as DuelSnapshotDto).id === 'string';

function socketChannel(socket: Socket): EventChannel<SocketEvent> {
	return eventChannel<SocketEvent>(emit => {
		const onConnect = () => emit({ type: 'connect' });
		const onDisconnect = (reason: string) => emit({ type: 'disconnect', reason });
		const onConnectError = () => emit({ type: 'connectError' });
		const onUpdated = (snapshot: unknown) => {
			if (isSnapshot(snapshot)) emit({ type: 'updated', snapshot });
		};
		const onJoined = (snapshot: unknown) => {
			if (isSnapshot(snapshot)) emit({ type: 'joined', snapshot });
		};
		const onError = (payload: { message?: string; code?: string } | undefined) =>
			emit({ type: 'error', message: payload?.message ?? '', code: payload?.code });
		const onReady = () => emit({ type: 'ready' });
		let appState: AppStateStatus = AppState.currentState;
		const onAppState = (next: AppStateStatus) => {
			if (appState !== 'active' && next === 'active') emit({ type: 'foreground' });
			appState = next;
		};

		socket.on('connect', onConnect);
		socket.on('disconnect', onDisconnect);
		socket.on('connect_error', onConnectError);
		socket.on('duels:updated', onUpdated);
		socket.on('duels:joined', onJoined);
		socket.on('duels:error', onError);
		socket.on('duels:ready', onReady);
		const appStateSubscription = AppState.addEventListener('change', onAppState);

		return () => {
			socket.off('connect', onConnect);
			socket.off('disconnect', onDisconnect);
			socket.off('connect_error', onConnectError);
			socket.off('duels:updated', onUpdated);
			socket.off('duels:joined', onJoined);
			socket.off('duels:error', onError);
			socket.off('duels:ready', onReady);
			appStateSubscription.remove();
		};
	});
}

function* socketSession() {
	const socket = openDuelsSocket();
	if (!socket) {
		yield put(duelsConnectionChanged('unavailable'));
		return;
	}

	const channel: EventChannel<SocketEvent> = yield call(socketChannel, socket);
	activeSocket = socket;
	let lastUpdateAt = 0;
	let updatedSinceRequest = false;
	const requestCurrent = () => {
		updatedSinceRequest = false;
		socket.emit('duels:getCurrent');
	};
	yield put(duelsConnectionChanged('connecting'));
	socket.connect();

	try {
		while (true) {
			const next: { event?: SocketEvent; reconnect?: unknown; refresh?: unknown } = yield race({
				event: take(channel),
				reconnect: take(duelsReconnectRequest.type),
				refresh: take(duelsCurrentRefresh.type),
			});

			if (next.reconnect) {
				yield put(duelsConnectionChanged('connecting'));
				if (!socket.connected) socket.connect();
				continue;
			}
			if (next.refresh) {
				if (socket.connected) requestCurrent();
				continue;
			}

			const event = next.event as SocketEvent;
			switch (event.type) {
				case 'connect':
					yield put(duelsConnectionChanged('connected'));
					requestCurrent();
					break;
				case 'disconnect':
					yield put(
						duelsConnectionChanged(
							event.reason === 'io server disconnect' ? 'replaced' : 'connecting',
						),
					);
					break;
				case 'connectError':
					yield put(duelsConnectionChanged('connecting'));
					break;
				case 'updated':
					lastUpdateAt = Date.now();
					updatedSinceRequest = true;
					yield put(duelsSnapshotReceived(event.snapshot));
					if (event.snapshot.status === 'completed') yield put(duelsHubRequest());
					break;
				case 'joined':
					yield put(duelsJoinSuccess(event.snapshot));
					break;
				case 'error':
					yield put(duelsSocketError({ message: event.message, code: event.code }));
					break;
				case 'ready':
					if (!updatedSinceRequest && Date.now() - lastUpdateAt > STALE_CURRENT_MS) {
						yield put(duelsCurrentCleared());
					}
					yield put(duelsCurrentReady());
					break;
				case 'foreground':
					if (socket.connected) requestCurrent();
					break;
			}
		}
	} finally {
		channel.close();
		activeSocket = null;
		closeDuelsSocket();
		yield put(duelsConnectionChanged('idle'));
	}
}

function* connectionLoop() {
	let mounted = 0;
	let session: Task | null = null;

	while (true) {
		const action: { type: string } = yield take([
			duelsScreenMounted.type,
			duelsScreenUnmounted.type,
			logout.type,
			loginSuccess.type,
		]);

		if (action.type === duelsScreenMounted.type) mounted += 1;
		if (action.type === duelsScreenUnmounted.type) mounted = Math.max(0, mounted - 1);

		const sessionChanged = action.type === logout.type || action.type === loginSuccess.type;
		if (session && (sessionChanged || mounted === 0)) {
			yield cancel(session);
			session = null;
		}

		const isLogged: boolean = yield select(selectIsLogged);
		if (!session && mounted > 0 && isLogged) {
			session = yield fork(socketSession);
		}
	}
}

function* joinWorker() {
	const connection: DuelsState['connection'] = yield select(selectDuelsConnection);
	if (!activeSocket || connection !== 'connected') {
		yield put(duelsJoinFailure(UNAVAILABLE_KEY));
		return;
	}

	activeSocket.emit('duels:join', { gameType: GAME_TYPE });

	const outcome: {
		joined?: ReturnType<typeof duelsJoinSuccess>;
		updated?: ReturnType<typeof duelsSnapshotReceived>;
		failed?: ReturnType<typeof duelsSocketError>;
		timedOut?: true;
	} = yield race({
		joined: take(duelsJoinSuccess.type),
		updated: take(
			(action: any) =>
				action.type === duelsSnapshotReceived.type && action.payload?.status !== 'completed',
		),
		failed: take(duelsSocketError.type),
		timedOut: delay(JOIN_TIMEOUT_MS),
		sessionChanged: take([logout.type, loginSuccess.type]),
	});

	if (outcome.updated) {
		yield put(duelsJoinSuccess(outcome.updated.payload));
	} else if (outcome.failed) {
		const code = outcome.failed.payload.code;
		yield put(duelsJoinFailure((code && JOIN_CODE_KEYS[code]) || JOIN_ERROR_KEY));
	} else if (outcome.timedOut) {
		yield put(duelsJoinFailure(JOIN_TIMEOUT_KEY));
	}
}

function* quitWorker(action: ReturnType<typeof duelsQuitRequest>) {
	const duelId = action.payload;
	const connection: DuelsState['connection'] = yield select(selectDuelsConnection);
	if (!activeSocket || connection !== 'connected') {
		yield put(duelsQuitFailure(QUIT_ERROR_KEY));
		return;
	}

	activeSocket.emit('duels:quit', { duelId });

	const outcome: {
		ended?: unknown;
		stopped?: unknown;
		failed?: unknown;
		timedOut?: true;
		sessionChanged?: unknown;
	} = yield race({
		ended: take(
			(candidate: any) =>
				candidate.type === duelsSnapshotReceived.type &&
				candidate.payload?.id === duelId &&
				(candidate.payload.currentPlayerFinished || candidate.payload.status === 'completed'),
		),
		stopped: take(duelsPlayStop.type),
		failed: take(duelsSocketError.type),
		timedOut: delay(QUIT_TIMEOUT_MS),
		sessionChanged: take([logout.type, loginSuccess.type]),
	});

	if (outcome.failed || outcome.timedOut) {
		yield put(duelsQuitFailure(QUIT_ERROR_KEY));
	} else if (!outcome.sessionChanged) {
		yield put(duelsQuitSettled());
	}
}

function* hubWorker() {
	try {
		const outcome: { data?: DuelHubResponse } = yield race({
			data: call(getDuelHub),
			sessionChanged: take([logout.type, loginSuccess.type]),
		});
		if (outcome.data) yield put(duelsHubSuccess(outcome.data));
	} catch (error: any) {
		yield put(duelsHubFailure(httpErrorKey(error, LOAD_ERROR_KEY)));
	}
}

const resultLags = (result: DuelResultResponse, push: DuelSnapshotDto | null): boolean => {
	if (!result.provisional) return result.settlement === null;
	if (!push) return false;
	if (push.status === 'completed') return true;
	if (push.currentPlayerFinished && !result.finished) return true;
	return (
		push.currentPlayerRank !== undefined &&
		push.currentPlayerRank !== null &&
		push.currentPlayerRank !== result.rank
	);
};

function* fetchResult(duelId: string) {
	try {
		const result: DuelResultResponse = yield call(getDuelResult, duelId);
		yield put(duelResultSuccess(result));
		return true;
	} catch (error: any) {
		const notFound = error?.response?.status === 404;
		yield put(
			duelResultFailure({
				duelId,
				key: notFound ? RESULT_NOT_FOUND_KEY : httpErrorKey(error, LOAD_ERROR_KEY),
			}),
		);
		return !notFound;
	}
}

function* resultPoll(duelId: string) {
	let attempt = 0;

	while (true) {
		const keepPolling: boolean = yield call(fetchResult, duelId);
		if (!keepPolling) return;

		const result: DuelResultResponse | null = yield select(selectDuelResult(duelId));
		const push: DuelSnapshotDto | null = yield select(selectDuelSnapshot(duelId));
		const lagging = !result || resultLags(result, push);
		const waitMs =
			lagging && attempt < RESULT_MAX_LAGGING_ATTEMPTS
				? RESULT_RETRY_DELAYS_MS[Math.min(attempt, RESULT_RETRY_DELAYS_MS.length - 1)]
				: null;
		attempt = lagging ? attempt + 1 : 0;

		const next: { pushed?: unknown } = yield race({
			pushed: take(
				(action: any) =>
					action.type === duelsSnapshotReceived.type && action.payload?.id === duelId,
			),
			...(waitMs !== null ? { waited: delay(waitMs) } : {}),
		});
		if (next.pushed) attempt = 0;
	}
}

function* resultWatcher() {
	const polls: Record<string, Task> = {};
	const watchers: Record<string, number> = {};

	while (true) {
		const action: ReturnType<typeof duelResultFocus> = yield take([
			duelResultFocus.type,
			duelResultBlur.type,
			duelResultRetry.type,
			logout.type,
			loginSuccess.type,
		]);
		const duelId = action.payload;

		if (action.type === duelResultFocus.type) {
			watchers[duelId] = (watchers[duelId] ?? 0) + 1;
			if (!polls[duelId]?.isRunning()) polls[duelId] = yield fork(resultPoll, duelId);
			continue;
		}

		if (action.type === duelResultRetry.type) {
			if (!watchers[duelId]) continue;
			if (polls[duelId]?.isRunning()) yield cancel(polls[duelId]);
			polls[duelId] = yield fork(resultPoll, duelId);
			continue;
		}

		const stopIds =
			action.type === duelResultBlur.type
				? (watchers[duelId] = Math.max(0, (watchers[duelId] ?? 0) - 1)) === 0
					? [duelId]
					: []
				: Object.keys(polls);
		for (const id of stopIds) {
			if (polls[id]) {
				yield cancel(polls[id]);
				delete polls[id];
			}
			delete watchers[id];
		}
	}
}

function* historySeasonId(filter: DuelHistoryFilter) {
	if (filter.tab === 'all') return undefined;

	if (filter.tab === 'current') {
		let hub: DuelHubResponse | null = yield select(selectDuelsHub);
		if (!hub) {
			hub = yield call(getDuelHub);
			yield put(duelsHubSuccess(hub as DuelHubResponse));
		}
		return hub?.season.season?.id ?? null;
	}

	const seasons: DuelPlayerSeasonDto[] = yield call(getDuelSeasons);
	yield put(duelsSeasonsSuccess(seasons ?? []));
	return seasons?.[0]?.season.id ?? null;
}

const EMPTY_PAGE: DuelHistoryResponse = { items: [], nextCursor: null, personalBests: [] };

function* historyWorker(action: ReturnType<typeof duelsHistoryRequest>) {
	const filter = action.payload;
	try {
		const outcome: { page?: DuelHistoryResponse } = yield race({
			page: call(function* () {
				const seasonId: string | null | undefined = yield call(historySeasonId, filter);
				if (seasonId === null) return EMPTY_PAGE;
				const page: DuelHistoryResponse = yield call(getDuelHistory, {
					limit: HISTORY_PAGE_SIZE,
					...(seasonId ? { seasonId } : {}),
					...(filter.duelTypeId ? { duelTypeId: filter.duelTypeId } : {}),
				});
				return page;
			}),
			sessionChanged: take([logout.type, loginSuccess.type]),
		});
		if (outcome.page) yield put(duelsHistorySuccess({ filter, page: outcome.page, append: false }));
	} catch (error: any) {
		yield put(duelsHistoryFailure(httpErrorKey(error, LOAD_ERROR_KEY)));
	}
}

function* historyMoreWorker() {
	const history: DuelsState['history'] = yield select(selectDuelsHistory);
	if (!history.nextCursor) return;

	const filter = history.filter;
	try {
		const seasonId: string | null | undefined = yield call(historySeasonId, filter);
		if (seasonId === null) return;
		const page: DuelHistoryResponse = yield call(getDuelHistory, {
			cursor: history.nextCursor,
			limit: HISTORY_PAGE_SIZE,
			...(seasonId ? { seasonId } : {}),
			...(filter.duelTypeId ? { duelTypeId: filter.duelTypeId } : {}),
		});
		yield put(duelsHistorySuccess({ filter, page, append: true }));
	} catch (error: any) {
		yield put(duelsHistoryFailure(httpErrorKey(error, LOAD_ERROR_KEY)));
	}
}

function* seasonWorker() {
	try {
		const outcome: { data?: DuelSeasonProgressResponse } = yield race({
			data: call(getDuelSeason),
			sessionChanged: take([logout.type, loginSuccess.type]),
		});
		if (outcome.data) yield put(duelsSeasonSuccess(outcome.data));
	} catch (error: any) {
		yield put(duelsSeasonFailure(httpErrorKey(error, LOAD_ERROR_KEY)));
	}
}

function* rewardsWorker() {
	try {
		const outcome: { data?: DuelPlayerRewardDto[] } = yield race({
			data: call(getDuelRewards),
			sessionChanged: take([logout.type, loginSuccess.type]),
		});
		if (outcome.data) yield put(duelsRewardsSuccess(outcome.data));
	} catch (error: any) {
		yield put(duelsRewardsFailure(httpErrorKey(error, LOAD_ERROR_KEY)));
	}
}

function* refreshAfterRewardWrite() {
	for (const waitMs of REWARD_REFRESH_DELAYS_MS) {
		yield delay(waitMs);
		yield call(rewardsWorker);
		const overlays: Record<string, unknown> = yield select(selectDuelRewardOverlays);
		if (!Object.keys(overlays).length) break;
	}
	yield put(duelsSeasonRequest());
	yield put(duelsHubRequest());
}

function* rewardTransitionWorker(
	action: ReturnType<typeof duelRewardRequestSubmit> | ReturnType<typeof duelRewardCancelSubmit>,
) {
	const isRequest = action.type === duelRewardRequestSubmit.type;
	const entitlementId = isRequest
		? (action as ReturnType<typeof duelRewardRequestSubmit>).payload.entitlementId
		: (action as ReturnType<typeof duelRewardCancelSubmit>).payload;
	const rewards: DuelPlayerRewardDto[] = yield select(selectDuelRewards);
	const before = rewards.find(reward => reward.id === entitlementId);

	try {
		const transition: DuelRewardTransitionDto = isRequest
			? yield call(requestDuelReward, entitlementId, {
					method: (action as ReturnType<typeof duelRewardRequestSubmit>).payload.method,
					email: (action as ReturnType<typeof duelRewardRequestSubmit>).payload.email,
				})
			: yield call(cancelDuelReward, entitlementId);
		yield put(
			duelRewardTransitionSuccess({
				id: transition.id,
				from: before?.status ?? 'Unlocked',
				status: transition.status,
				kind: isRequest ? 'request' : 'cancel',
			}),
		);
	} catch (error: any) {
		yield put(duelRewardTransitionFailure(rewardErrorKey(error)));
	}
	yield spawn(refreshAfterRewardWrite);
}

function* rewardClaimWorker(action: ReturnType<typeof duelRewardClaimSubmit>) {
	const entitlementId = action.payload;
	const rewards: DuelPlayerRewardDto[] = yield select(selectDuelRewards);
	const before = rewards.find(reward => reward.id === entitlementId);

	try {
		const transition: DuelRewardTransitionDto = yield call(claimDuelReward, entitlementId);
		yield put(
			duelRewardTransitionSuccess({
				id: transition.id,
				from: before?.status ?? 'Unlocked',
				status: transition.status,
				kind: 'claim',
			}),
		);
	} catch (error: any) {
		yield put(duelRewardClaimFailure({ id: entitlementId, key: rewardErrorKey(error) }));
	}
	yield spawn(refreshAfterRewardWrite);
}

function* rankingWorker(action: ReturnType<typeof duelsRankingRequest>) {
	const scope: DuelRankingScope = action.payload;
	try {
		const outcome: { board?: DuelSeasonLeaderboardResponse | null } = yield race({
			board: call(function* () {
				if (scope === 'current') {
					const current: DuelSeasonLeaderboardResponse = yield call(
						getDuelSeasonRanking,
						'current',
					);
					return current;
				}
				const seasons: DuelPlayerSeasonDto[] = yield call(getDuelSeasons);
				yield put(duelsSeasonsSuccess(seasons ?? []));
				const previousId = seasons?.[0]?.season.id;
				if (!previousId) return null;
				const previous: DuelSeasonLeaderboardResponse = yield call(
					getDuelSeasonRanking,
					previousId,
				);
				return previous;
			}),
			sessionChanged: take([logout.type, loginSuccess.type]),
		});
		if (outcome.board !== undefined)
			yield put(duelsRankingSuccess({ scope, board: outcome.board }));
	} catch (error: any) {
		yield put(duelsRankingFailure({ scope, key: httpErrorKey(error, LOAD_ERROR_KEY) }));
	}
}

export function* duelsRootSaga() {
	yield fork(connectionLoop);
	yield fork(resultWatcher);
	yield takeLatest(duelsJoinRequest.type, joinWorker);
	yield takeLeading(duelsQuitRequest.type, quitWorker);
	yield takeLatest(duelsHubRequest.type, hubWorker);
	yield takeLatest(duelsHistoryRequest.type, historyWorker);
	yield takeLeading(duelsHistoryLoadMore.type, historyMoreWorker);
	yield takeLatest(duelsSeasonRequest.type, seasonWorker);
	yield takeLatest(duelsRewardsRequest.type, rewardsWorker);
	yield takeLeading(
		[duelRewardRequestSubmit.type, duelRewardCancelSubmit.type],
		rewardTransitionWorker,
	);
	yield takeEvery(duelRewardClaimSubmit.type, rewardClaimWorker);
	yield takeEvery(duelsRankingRequest.type, rankingWorker);
}
